import { useEffect, useRef, useState } from 'react';
import { API } from '@unboared/react';

/** A gamepad that has been `device_away` for at least this long (with no `device_back`)
    is treated as gone for free-play decisions (reset to idle, vote quorum, chooser).
    Deliberately shorter than the server grace: the V2 engine still preserves the
    player's full session for its own (longer) grace, so a reconnection within that
    window restores them intact. A transient outage fires device_away → device_back
    well inside this window, so a micro-coupure never drops a player. */
export const FREE_PLAY_AWAY_GRACE_MS = 15000;

/** Longer grace used only for the destructive reset-to-idle (tearing down the running
    game back to the QR attract screen). A lone player who steps away — e.g. to answer a
    text — must be able to come back to their game, so this is generous; but it stays
    well under the server grace so an abandoned kiosk returns to the attract screen
    quickly enough to catch the next passenger. Decoupled from the short grace above,
    which only governs the reversible vote quorum / chooser hand-off (decision 2026-06-19). */
export const FREE_PLAY_ABANDON_MS = 90000;

/**
 * Tracks which gamepads are *effectively* present for free-play, layering a short
 * console-side grace on top of the lib's away/back events.
 *
 * Why this exists: `getGamepadIDs()` keeps a device in the list until the server's
 * grace period expires (5 min in auto, 6 h in anim) — so a player who closes their
 * tab still counts as connected for minutes/hours, which freezes the free-play reset,
 * vote quorum and chooser hand-off. The lib does, however, fire `onDeviceAway` /
 * `onDeviceBack` in real time, so the console can react fast on its own.
 *
 * `device_away` alone is NOT a departure (it fires on a tab close AND on a network
 * blip), so we never drop on the event itself: a device only counts as gone once it
 * has stayed away past the grace without coming back. This governs only the live tally
 * and reverses instantly on `device_back` — the engine's session preservation is
 * untouched, so its work is never wasted.
 */
export function useFreePlayPresence(enabled = true, graceMs = FREE_PLAY_AWAY_GRACE_MS) {
    /** deviceID → epoch ms at which it was first seen away (absent from the list). */
    const awaySinceRef = useRef<Map<string, number>>(new Map());
    /** deviceID → pending re-render timer scheduled for when it crosses the grace. */
    const timersRef = useRef<Map<string, number>>(new Map());
    const [, forceRender] = useState(0);

    useEffect(() => {
        if (!enabled) return undefined;
        const bump = () => forceRender((n) => n + 1);
        const clearTimer = (deviceID: string) => {
            const t = timersRef.current.get(deviceID);
            if (t !== undefined) {
                window.clearTimeout(t);
                timersRef.current.delete(deviceID);
            }
        };
        const markBack = (deviceID: string) => {
            const had = awaySinceRef.current.delete(deviceID);
            clearTimer(deviceID);
            if (had) bump();
        };
        const offAway = API.onDeviceAway((deviceID: string) => {
            if (awaySinceRef.current.has(deviceID)) return;
            awaySinceRef.current.set(deviceID, Date.now());
            // Re-render exactly when this device crosses the grace, so consumers
            // re-evaluate even if no other event arrives in the meantime.
            const timer = window.setTimeout(() => {
                timersRef.current.delete(deviceID);
                bump();
            }, graceMs);
            timersRef.current.set(deviceID, timer);
            bump();
        });
        // A returning device dispatches onDeviceBack to peers; onConnect covers a brand
        // new device taking the same slot. Either way, it is present again.
        const offBack = API.onDeviceBack((deviceID: string) => markBack(deviceID));
        const offConnect = API.onConnect((deviceID: string) => markBack(deviceID));
        return () => {
            offAway();
            offBack();
            offConnect();
            timersRef.current.forEach((t) => window.clearTimeout(t));
            timersRef.current.clear();
            awaySinceRef.current.clear();
        };
    }, [enabled, graceMs]);

    /** Gamepad IDs present right now: connected AND not away past the grace. Reads live
        state through refs, so it is safe to call from render or from an interval.
        Pass a custom grace (e.g. FREE_PLAY_ABANDON_MS) to evaluate presence against a
        different threshold than the hook default — both read the same away clock.

        PURE: no side effects, so it is safe in a render path (GamepadManager computes
        the chooser from it). A stale away marker for a device the server has fully
        removed simply lingers in the map (it is never consulted — the filter only looks
        at ids currently in getGamepadIDs); and if that id is reused by a fresh device,
        onConnect/onDeviceBack clears the marker before it can mis-flag it as gone. */
    const getPresentGamepadIDs = (graceOverrideMs: number = graceMs): string[] => {
        let ids: string[];
        try {
            ids = API.getGamepadIDs();
        } catch {
            return [];
        }
        const now = Date.now();
        const away = awaySinceRef.current;
        return ids.filter((id) => {
            const since = away.get(id);
            return since === undefined || now - since < graceOverrideMs;
        });
    };

    return { getPresentGamepadIDs };
}
